import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useSetTaskState } from '../../hooks/useTasks';
import { chipTextColor } from '../../utils/format';
import { AppToast, type AppToastState } from '../app-access/AppToast';
import type { TaskWithStats } from '../../services/api/tasks';
import type { TaskState } from '../../services/api/projectStates';

export interface TaskStateChipProps {
  task: TaskWithStats;
  projectId: number;
  states: TaskState[];
  editable: boolean;
}

/**
 * Per-task colored status chip + admin/PM inline-edit popover (Phase 19).
 *
 * Owns:
 *  - Chip rendering (D-13, D-14): backgroundColor = state.color,
 *    color = chipTextColor(state.color).
 *  - Read-only vs editable visual distinction (D-15, D-16).
 *  - Null-state rendering (D-17): '—' in muted gray.
 *  - Hand-rolled popover (D-18): mousedown-outside / Escape close;
 *    pattern mirrors CellEntriesPopover.
 *  - Per-row mini-swatch + check icon (D-19).
 *  - Optimistic mutation wiring (D-20) via useSetTaskState.
 *  - AppToast error surface (D-21).
 *
 * The `editable` boolean is computed by the parent (TaskManagementPage)
 * from D-22: `currentUser.role === 'admin' || project.projectManager?.id === currentUser.id`.
 * The chip itself is pure-presentational w.r.t. permissions.
 *
 * **Phase 17 D-12 dual-write fallback (Blocker 1 resolution B1):**
 * Phase 18's `getManagerTasks` endpoint does NOT return `taskStateId` (the
 * FK). Without a fallback every chip would render '—'. Phase 17 D-12 locks
 * `tasks.status === task_states.name` for every task with a state, so we
 * resolve `currentState` by FK first, then by name-match. After
 * `useSetTaskState`'s onSettled invalidation refetches `manager/tasks`
 * (still without taskStateId), the dual-write means `task.status` carries
 * the new state's canonical name, so the chip resolves correctly via the
 * fallback. If a future v5.1 phase removes the dual-write OR refactors
 * `getManagerTasks` to return `taskStateId`, this fallback becomes
 * vestigial but is still correct (priority 1 would always win).
 */
export function TaskStateChip({ task, projectId, states, editable }: TaskStateChipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [toast, setToast] = useState<AppToastState | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const setTaskStateMutation = useSetTaskState();

  // Resolve current state — Phase 17 D-12 dual-write makes the name-match
  // safe and necessary because Phase 18's getManagerTasks does not return
  // taskStateId. See file-level JSDoc above for the full reasoning.
  const currentState = useMemo<TaskState | null>(() => {
    if (task.taskStateId != null) {
      return states.find((s) => s.id === task.taskStateId) ?? null;
    }
    if (task.status) {
      // Phase 17 D-12 dual-write fallback: getManagerTasks does not return
      // taskStateId, but task.status is kept in sync with task_states.name.
      return states.find((s) => s.name === task.status) ?? null;
    }
    return null;
  }, [task.taskStateId, task.status, states]);

  // Click-outside + Escape close — mirrors CellEntriesPopover (D-18).
  useEffect(() => {
    if (!isOpen) return;
    const handleMousedown = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    // Defer attaching mousedown to next tick so the click that opened the
    // popover doesn't immediately close it (CellEntriesPopover precedent).
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleMousedown);
      document.addEventListener('keydown', handleKeydown);
    }, 0);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleMousedown);
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [isOpen]);

  const handleChipClick = () => {
    if (!editable) return;
    setIsOpen((prev: boolean) => !prev);
  };

  const handleSelect = (stateId: number) => {
    setIsOpen(false);
    // No-op when picking the currently-resolved state. Compare against the
    // RESOLVED currentState (FK or name-fallback), not raw task.taskStateId,
    // so picking the same state is a no-op even when the FK is missing
    // from the response.
    if (currentState && stateId === currentState.id) return;
    setTaskStateMutation.mutate(
      { projectId, taskId: task.id, taskStateId: stateId },
      {
        onError: (err: Error) => {
          setToast({
            variant: 'error',
            message: err.message || 'Failed to change task state',
          });
        },
      }
    );
  };

  // ---------- Render: null currentState path (D-17) ----------
  if (currentState === null) {
    if (!editable) {
      return <span className="text-sm text-gray-400">—</span>;
    }
    return (
      <div ref={containerRef} className="relative inline-block">
        <button
          type="button"
          onClick={handleChipClick}
          className="text-sm text-gray-400 hover:text-gray-600 cursor-pointer"
          aria-label="Set task state"
        >
          —
        </button>
        {isOpen && (
          <Popover
            popoverRef={popoverRef}
            anchorRef={containerRef}
            states={states}
            currentStateId={null}
            onSelect={handleSelect}
          />
        )}
        <AppToast toast={toast} onDismiss={() => setToast(null)} />
      </div>
    );
  }

  // ---------- Render: chip path (D-14, D-15, D-16) ----------
  const chipBg = currentState.color;
  const chipFg = chipTextColor(currentState.color);

  const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
  const interactiveClasses = editable
    ? 'cursor-pointer hover:ring-2 hover:ring-offset-1'
    : 'cursor-default';

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={handleChipClick}
        disabled={!editable}
        aria-haspopup={editable ? 'listbox' : undefined}
        aria-expanded={editable ? isOpen : undefined}
        aria-label={editable ? `Change state from ${currentState.name}` : currentState.name}
        className={`${baseClasses} ${interactiveClasses}`}
        style={{ backgroundColor: chipBg, color: chipFg }}
      >
        {currentState.name}
        {editable && <ChevronDownIcon className="ml-1 h-3 w-3" />}
      </button>
      {isOpen && editable && (
        <Popover
          popoverRef={popoverRef}
          anchorRef={containerRef}
          states={states}
          currentStateId={currentState.id}
          onSelect={handleSelect}
        />
      )}
      <AppToast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}

// -----------------------------------------------------------------------
// Internal popover sub-component (D-18, D-19)
// -----------------------------------------------------------------------

interface PopoverProps {
  popoverRef: React.RefObject<HTMLDivElement>;
  anchorRef: React.RefObject<HTMLDivElement>;
  states: TaskState[];
  currentStateId: number | null;
  onSelect: (stateId: number) => void;
}

function Popover({ popoverRef, anchorRef, states, currentStateId, onSelect }: PopoverProps) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
  }, [anchorRef]);

  if (!pos) return null;

  return createPortal(
    <div
      ref={popoverRef}
      role="listbox"
      style={{ position: 'absolute', top: pos.top, left: pos.left }}
      className="z-50 bg-white rounded-lg shadow-xl border border-gray-200 min-w-[160px] py-1"
    >
      {states.map((state) => {
        const isCurrent = state.id === currentStateId;
        return (
          <button
            key={state.id}
            type="button"
            role="option"
            aria-selected={isCurrent}
            onClick={() => onSelect(state.id)}
            className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm text-left hover:bg-gray-50"
          >
            <span className="flex items-center gap-2">
              <span
                className="w-3 h-3 rounded-full inline-block"
                style={{ backgroundColor: state.color }}
                aria-hidden="true"
              />
              <span>{state.name}</span>
            </span>
            {isCurrent && <CheckIcon className="h-4 w-4 text-gray-500" />}
          </button>
        );
      })}
    </div>,
    document.body
  );
}
